/**
 * Rest timers.
 *
 * **A rest timer is an absolute instant, not a countdown.**
 *
 * iOS suspends timers whenever the screen locks or the app is backgrounded —
 * which, between sets, is most of the time. A timer that subtracted a second
 * per tick would be wrong by exactly however long the phone was asleep, and it
 * would be wrong in the direction that tells the user to rest longer than they
 * did. Storing `endsAt` and deriving the remaining time from the current
 * instant is correct whether the app ran continuously, was backgrounded, or was
 * killed and relaunched.
 *
 * `now` is always a parameter: this module is in `@gym/core`, which never reads
 * the clock itself.
 */

export interface RestTimer {
  /** When the rest began. Epoch milliseconds, UTC. */
  startedAt: number;
  /** When it is due to end. Epoch milliseconds, UTC. */
  endsAt: number;
  /** The prescribed length, kept so an extension can be told from the original. */
  durationS: number;
}

export class InvalidRestTimerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidRestTimerError';
  }
}

function assertFinite(value: number, what: string): void {
  if (!Number.isFinite(value)) {
    throw new InvalidRestTimerError(`${what} must be a finite number, received: ${String(value)}`);
  }
}

export function startRest(nowEpochMs: number, durationS: number): RestTimer {
  assertFinite(nowEpochMs, 'The current time');
  assertFinite(durationS, 'Rest duration');
  if (durationS < 0) {
    throw new InvalidRestTimerError(`Rest duration cannot be negative: ${String(durationS)}`);
  }

  return {
    startedAt: nowEpochMs,
    endsAt: nowEpochMs + Math.round(durationS * 1000),
    durationS,
  };
}

/**
 * Seconds still to go, rounded up so "1" is shown until the timer truly ends.
 *
 * Never negative: once the rest is over the answer is 0, and how far past the
 * end it is belongs to `overdueSeconds`.
 */
export function restRemainingSeconds(timer: RestTimer, nowEpochMs: number): number {
  assertFinite(nowEpochMs, 'The current time');
  return Math.max(0, Math.ceil((timer.endsAt - nowEpochMs) / 1000));
}

/** Seconds elapsed since the timer ended. 0 while it is still running. */
export function restOverdueSeconds(timer: RestTimer, nowEpochMs: number): number {
  assertFinite(nowEpochMs, 'The current time');
  return Math.max(0, Math.floor((nowEpochMs - timer.endsAt) / 1000));
}

export function isRestComplete(timer: RestTimer, nowEpochMs: number): boolean {
  return nowEpochMs >= timer.endsAt;
}

/**
 * Fraction of the rest that has passed, clamped to 0…1.
 *
 * A zero-length rest is complete the moment it starts, which is 1 rather than a
 * division by zero.
 */
export function restProgress(timer: RestTimer, nowEpochMs: number): number {
  const total = timer.endsAt - timer.startedAt;
  if (total <= 0) return 1;
  return Math.min(1, Math.max(0, (nowEpochMs - timer.startedAt) / total));
}

/**
 * Add (or, with a negative delta, remove) time.
 *
 * Adjusts `endsAt` rather than restarting, so the "+30 s" button extends the
 * rest instead of resetting it. The end can never be moved before the start.
 */
export function adjustRest(timer: RestTimer, deltaS: number): RestTimer {
  assertFinite(deltaS, 'Adjustment');
  const endsAt = Math.max(timer.startedAt, timer.endsAt + Math.round(deltaS * 1000));
  return { startedAt: timer.startedAt, endsAt, durationS: (endsAt - timer.startedAt) / 1000 };
}

/** End the rest now. Used by "skip rest". */
export function endRestNow(timer: RestTimer, nowEpochMs: number): RestTimer {
  const endsAt = Math.max(timer.startedAt, nowEpochMs);
  return { startedAt: timer.startedAt, endsAt, durationS: (endsAt - timer.startedAt) / 1000 };
}

/**
 * How long a rest actually lasted.
 *
 * Measured against when the next set began, not against `endsAt` — the useful
 * number is what was really rested, which is usually longer than prescribed.
 */
export function actualRestSeconds(timer: RestTimer, nextSetAtEpochMs: number): number {
  assertFinite(nextSetAtEpochMs, 'The next set time');
  return Math.max(0, Math.round((nextSetAtEpochMs - timer.startedAt) / 1000));
}
